import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchQuestions,
  refreshQuestionStructure,
  deleteAnswerFile,
  submitAnswers,
  setAnswer,
  completeOnboardingStep,
  fetchOnboardingStatus,
} from '../../store/slices/onboardingSlice';
import { evaluateConditionalRules } from '../../utils/conditionalEngine';
import { validateByType, validateTableCell, isConditionallyRequiredTableCell } from '../../utils/validation';
import QuestionField from './QuestionField';
import HelpTip from '../common/HelpTip';

function QuestionsStep({ step, onBack, isFirstStep }) {
  const dispatch = useDispatch();
  const { questionGroups, answers, answerVersions, loading, countryCode } = useSelector((state) => state.onboarding);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [draftSaved, setDraftSaved] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [tableCellErrors, setTableCellErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  // AI document validation: per-question failures from the last save attempt,
  // and the user's justification texts for overriding them.
  const [docValidationErrors, setDocValidationErrors] = useState({});
  const [docJustifications, setDocJustifications] = useState({});

  // File answers stored outside Redux (File objects are not serializable)
  const fileAnswersRef = useRef({});

  useEffect(() => {
    dispatch(fetchQuestions());
  }, [dispatch]);

  // Conditional rules are only loaded when this step mounts, so a client
  // sitting on the form kept the old logic after an admin changed a rule
  // (EOP-96). Pick up structure changes when the tab regains focus — answers
  // are untouched, so nothing typed-but-unsaved is lost.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        dispatch(refreshQuestionStructure());
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dispatch]);

  // Answers augmented with virtual fields (country of incorporation) that
  // conditional rules can key off via parent_field.
  const evalAnswers = useMemo(
    () => ({ ...answers, country_code: countryCode }),
    [answers, countryCode]
  );

  const isQuestionVisible = (question) => {
    if (!question.conditional_rules || question.conditional_rules.length === 0) {
      return true;
    }
    return evaluateConditionalRules(question.conditional_rules, evalAnswers);
  };

  // Same test by id, for deciding what belongs in a save. A question we can't
  // find (another step's) is left alone rather than dropped.
  const isQuestionAnswerable = (questionId) => {
    for (const group of questionGroups) {
      const question = group.questions.find((q) => q.id === questionId);
      if (question) return isQuestionVisible(question);
    }

    return true;
  };

  // Reorder a group's questions so any conditional child is emitted directly
  // after the parent referenced by its rule (when the parent lives in the
  // same group). Children whose parent lives elsewhere keep their natural
  // backend order.
  const reorderQuestionsWithChildren = (questions) => {
    if (!Array.isArray(questions) || questions.length === 0) return [];

    const byId = new Map(questions.map((q) => [q.id, q]));
    const childrenByParent = new Map();
    const anchored = new Set();

    questions.forEach((q) => {
      const rule = q.conditional_rules && q.conditional_rules[0];
      if (rule && byId.has(rule.parent_question_id)) {
        const parentId = rule.parent_question_id;
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId).push(q);
        anchored.add(q.id);
      }
    });

    const result = [];
    const seen = new Set();

    const emit = (q) => {
      if (seen.has(q.id)) return;
      seen.add(q.id);
      result.push(q);
      const kids = childrenByParent.get(q.id);
      if (kids && kids.length) {
        const sorted = [...kids].sort((a, b) => (a.order || 0) - (b.order || 0));
        sorted.forEach(emit);
      }
    };

    questions.forEach((q) => {
      if (anchored.has(q.id)) return;
      emit(q);
    });

    return result;
  };

  // A section step may scope itself to specific question groups (by slug) via
  // step.config.groups; otherwise all groups are shown.
  const sectionSlugs = step?.config?.groups;

  // Filter out groups that have no visible questions; restrict + order by the
  // section's configured groups when present.
  const visibleGroups = useMemo(() => {
    let groups = questionGroups;
    if (Array.isArray(sectionSlugs) && sectionSlugs.length > 0) {
      const orderBySlug = new Map(sectionSlugs.map((slug, i) => [slug, i]));
      groups = questionGroups
        .filter((g) => orderBySlug.has(g.slug))
        .sort((a, b) => orderBySlug.get(a.slug) - orderBySlug.get(b.slug));
    }
    return groups.filter((group) => group.questions.some((q) => isQuestionVisible(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionGroups, answers, sectionSlugs]);

  const activeGroup = visibleGroups[activeGroupIndex];
  const activeQuestions = useMemo(() => {
    if (!activeGroup) return [];
    return reorderQuestionsWithChildren(activeGroup.questions).filter(isQuestionVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup, answers]);
  const isLastGroup = activeGroupIndex === visibleGroups.length - 1;
  const isFirstGroup = activeGroupIndex === 0;

  // Defence in depth: if conditional visibility shrinks the group list below
  // the current index, clamp it so the page never renders blank.
  useEffect(() => {
    if (visibleGroups.length > 0 && activeGroupIndex > visibleGroups.length - 1) {
      setActiveGroupIndex(visibleGroups.length - 1);
    }
  }, [visibleGroups.length, activeGroupIndex]);

  // Errors belong to the group that produced them. Only a successful save
  // cleared the document-validation panel, so an upload error followed by
  // Back kept an amber warning pinned to a question on another page
  // (EOP-40). The step spans several groups without remounting, so clear
  // them whenever the group changes.
  useEffect(() => {
    setValidationErrors({});
    setTableCellErrors({});
    setDocValidationErrors({});
    setSubmitError(null);
  }, [activeGroupIndex, step.id]);

  // Build a set of file-type question IDs for quick lookup
  const fileQuestionIds = useMemo(() => {
    const ids = new Set();
    questionGroups.forEach((group) => {
      group.questions.forEach((q) => {
        if (q.type === 'file') ids.add(q.id);
      });
    });
    return ids;
  }, [questionGroups]);

  const handleAnswerChange = useCallback((questionId, value) => {
    if (fileQuestionIds.has(questionId)) {
      // Store File objects in ref (not Redux)
      fileAnswersRef.current[questionId] = value;
      // Marker dispatched to Redux must change whenever the selection
      // changes, otherwise Immer/useSelector skip the update and the
      // re-render that surfaces the new ref value never happens. Encode
      // the file count + each file's name/size so add, remove, and
      // replace all yield a different marker string.
      let marker = '';
      if (Array.isArray(value) && value.length > 0) {
        const sig = value.map((f) => `${f?.name ?? ''}|${f?.size ?? ''}`).join(',');
        marker = `__files__:${value.length}:${sig}`;
      }
      dispatch(setAnswer({ questionId, value: marker }));
    } else {
      dispatch(setAnswer({ questionId, value }));
    }
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
    setTableCellErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, [dispatch, fileQuestionIds, validationErrors]);

  const isCellFilled = (v) => {
    if (v === null || v === undefined || v === '') return false;
    // Whitespace alone is not an answer — otherwise a single space in one cell
    // satisfied every required check on the row (EOP-25).
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  };

  const getTableRows = (val) => {
    let rows = val;
    if (typeof rows === 'string') {
      try { rows = JSON.parse(rows); } catch { rows = []; }
    }
    return Array.isArray(rows) ? rows : [];
  };

  // Returns { message, cells } when invalid, otherwise null.
  // `cells` maps `${rowIndex}_${columnKey}` -> error message string.
  const validateTableQuestion = (question) => {
    const columns = (question.options && question.options.columns) || [];
    const rows = getTableRows(answers[question.id]);
    const requiredColumns = columns.filter((c) => c.required);
    const isRowEmpty = (row) => !columns.some((col) => isCellFilled(row?.[col.key]));
    const filledRows = rows.filter((r) => !isRowEmpty(r));
    // Treat the table as required when EITHER the question itself is
    // marked required OR any column is required. Otherwise a conditional
    // table with required columns but an unticked "Required" checkbox
    // lets users advance past an empty table without filling anything.
    const mustBeFilled = question.is_required || requiredColumns.length > 0;
    const requiredAndEmpty = mustBeFilled && filledRows.length === 0;

    const cells = {};

    // Ensure the first row's required cells are flagged when the question is
    // required but the user hasn't filled anything yet (rows may not even exist
    // in the answer store until the first edit).
    if (requiredAndEmpty) {
      requiredColumns.forEach((col) => {
        cells[`0_${col.key}`] = 'This field is required.';
      });
    }

    // Rows up to min_rows are scaffolding the renderer pads in; anything
    // beyond that the user explicitly added with "Add Entry" and must either
    // complete or remove. Skipping every empty row past index 0 let a blank
    // added entry through unvalidated (EOP-25, EOP-28, EOP-35).
    const minRows = Number(question.options?.min_rows) || 1;

    rows.forEach((row, rowIndex) => {
      const rowEmpty = isRowEmpty(row);
      if (rowEmpty && rowIndex !== 0 && rowIndex < minRows) return;

      columns.forEach((col) => {
        const cellValue = row?.[col.key];
        const conditionMet = isConditionallyRequiredTableCell(col, row);
        // Required check first (handled per row).
        if ((col.required || conditionMet) && !isCellFilled(cellValue)) {
          cells[`${rowIndex}_${col.key}`] = 'This field is required.';
          return;
        }
        // Skip type-level validation when the cell is empty and not required —
        // empty optional cells should never produce an error.
        if (!isCellFilled(cellValue)) return;
        const error = validateTableCell(col, cellValue);
        if (error) {
          cells[`${rowIndex}_${col.key}`] = error;
        }
      });
    });

    if (requiredAndEmpty) {
      return { message: 'This field is required.', cells };
    }
    if (Object.keys(cells).length > 0) {
      const anyTypeError = Object.values(cells).some(
        (msg) => msg && msg !== 'This field is required.'
      );
      return {
        message: anyTypeError
          ? 'Please correct the highlighted fields in the table.'
          : 'Please fill all required fields in the table.',
        cells,
      };
    }
    return null;
  };

  const isAnswerEmpty = (question) => {
    // For file questions, check if new files selected or existing server files present
    if (question.type === 'file') {
      const newFiles = fileAnswersRef.current[question.id];
      if (Array.isArray(newFiles) && newFiles.length > 0) return false;
      if (question.files && question.files.length > 0) return false;
      // After a successful save the ref is cleared and Redux's
      // state.questionGroups isn't refreshed (we only refetch on Back),
      // so question.files stays stale even though the file is actually
      // saved server-side. Trust the marker we kept in state.answers as
      // a non-empty signal so validation across pages doesn't bounce
      // the user back to a page whose file already uploaded.
      const stored = answers[question.id];
      if (typeof stored === 'string' && stored.startsWith('__files__')) return false;
      return true;
    }
    const val = answers[question.id];
    if (typeof val === 'string') return val.trim() === '';
    if (Array.isArray(val)) return val.length === 0;
    // Address / UBO answers are objects. Every branch below was false for any
    // object, so merely picking a country from the dropdown satisfied a
    // required Registered Address (EOP-17).
    if (val && typeof val === 'object') {
      return !Object.values(val).some((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;

        return true;
      });
    }
    return val === undefined || val === null || val === '';
  };

  const collectErrors = (questions) => {
    const errors = {};
    const cellErrors = {};
    questions.forEach((question) => {
      if (question.type === 'table') {
        const result = validateTableQuestion(question);
        if (result) {
          errors[question.id] = result.message;
          if (Object.keys(result.cells).length > 0) {
            cellErrors[question.id] = result.cells;
          }
        }
        return;
      }
      const conditionallyRequired = (question.conditional_rules || [])
        .some((rule) => (rule.action || 'show') === 'show');
      const required = question.is_required || conditionallyRequired;
      if (required && isAnswerEmpty(question)) {
        errors[question.id] = 'This field is required.';
        return;
      }
      // File-type required-ness is handled above; per-question type rules
      // (regex/min/max/date) only apply when there's a value to check.
      if (question.type === 'file') return;
      const value = answers[question.id];
      // A required address means every part of it. This path calls
      // validateByType directly rather than validateQuestion, so the flag has
      // to be passed through or a part-filled address slips by (report item 2).
      const typeError = validateByType(question.type, value, question.validation_rules, {
        requireAll: !!required,
      });
      if (typeError) {
        errors[question.id] = typeError;
      }
    });
    return { errors, cellErrors };
  };

  const validateCurrentGroup = () => collectErrors(activeQuestions);

  const validateAllGroups = () => {
    const allQuestions = [];
    visibleGroups.forEach((group) => {
      group.questions.forEach((question) => {
        if (isQuestionVisible(question)) allQuestions.push(question);
      });
    });
    return collectErrors(allQuestions);
  };

  // Map of table-question id -> question (for column-type lookups during save).
  const tableQuestionMap = useMemo(() => {
    const map = {};
    questionGroups.forEach((g) =>
      g.questions.forEach((q) => {
        if (q.type === 'table') map[q.id] = q;
      })
    );
    return map;
  }, [questionGroups]);

  const handleSave = async () => {
    setSubmitError(null);

    const tableFilePayload = [];

    // Separate non-file answers from file answers, extracting any File objects
    // that were dropped into table cells so they can be uploaded as multipart.
    const answersPayload = Object.entries(answers)
      .filter(([questionId]) => !fileQuestionIds.has(parseInt(questionId)))
      // A question hidden by a conditional rule is not part of this
      // application, but its answer was still submitted on every save — so
      // answering "No" left the "Yes" data attached (EOP-41). The stored value
      // is kept, just detached, so a toggle can't destroy anything.
      .filter(([questionId]) => isQuestionAnswerable(parseInt(questionId)))
      .map(([questionId, value]) => {
        const qid = parseInt(questionId);
        const question = tableQuestionMap[qid];
        if (!question) {
          return { question_id: qid, value };
        }

        let rows = value;
        if (typeof rows === 'string') {
          try { rows = JSON.parse(rows); } catch { rows = []; }
        }
        if (!Array.isArray(rows)) {
          return { question_id: qid, value };
        }

        const fileColumnKeys = ((question.options && question.options.columns) || [])
          .filter((c) => c.type === 'file')
          .map((c) => c.key);

        if (fileColumnKeys.length === 0) {
          return { question_id: qid, value: rows };
        }

        const cleanedRows = rows.map((row, rowIndex) => {
          const cleaned = { ...(row || {}) };
          fileColumnKeys.forEach((columnKey) => {
            const cellValue = cleaned[columnKey];
            if (cellValue instanceof File) {
              tableFilePayload.push({ questionId: qid, rowIndex, columnKey, file: cellValue });
              cleaned[columnKey] = '';
            }
          });
          return cleaned;
        });

        return { question_id: qid, value: cleanedRows };
      })
      // Attach the version we loaded so the server can reject a save that
      // would overwrite a collaborator's newer answer (EOP-97).
      .map((entry) => ({ ...entry, version: answerVersions[entry.question_id] ?? null }));

    // Collect file answers (only those with actual File objects)
    const filePayload = {};
    Object.entries(fileAnswersRef.current).forEach(([questionId, files]) => {
      // Same rule for uploads: don't attach a document to a question the
      // client's answers have hidden (EOP-41).
      if (Array.isArray(files) && files.length > 0 && isQuestionAnswerable(parseInt(questionId))) {
        filePayload[questionId] = files;
      }
    });

    const result = await dispatch(submitAnswers({
      answers: answersPayload,
      fileAnswers: filePayload,
      tableFileAnswers: tableFilePayload,
      fileJustifications: docJustifications,
    }));
    if (!result.error) {
      // Clear file ref after successful upload
      fileAnswersRef.current = {};
      setDocValidationErrors({});
      setDocJustifications({});
      // Pick up the versions our own write produced, so the next save still
      // has conflict detection (EOP-97). Answers are untouched by this.
      dispatch(refreshQuestionStructure());
      return true;
    }

    const docFailures = result.payload?.documentValidation;
    if (docFailures) {
      setDocValidationErrors(docFailures);
      setSubmitError('One or more documents did not pass validation. Review the highlighted uploads below.');
      return false;
    }

    // A collaborator saved first — say who and what, and don't pretend the
    // save worked (EOP-97).
    if (result.payload?.code === 'answer_conflict') {
      setSubmitError(result.payload.message);
      return false;
    }

    setSubmitError('Failed to save answers. Please try again.');
    return false;
  };

  const handleNextGroup = async () => {
    const { errors, cellErrors } = validateCurrentGroup();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTableCellErrors(cellErrors);
      return;
    }
    setTableCellErrors({});

    // Auto-save on group navigation. Only advance if the save succeeded —
    // a failed/rejected document upload must not let the user skip past a
    // mandatory document (EOP-51). Mirrors handleSubmitAll's gating.
    const saved = await handleSave();
    if (!saved) return;
    setActiveGroupIndex((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevGroup = async () => {
    if (isFirstGroup) {
      onBack();
      return;
    }
    setTableCellErrors({});
    setValidationErrors({});
    // 1) Persist any pending edits in the current group so they aren't
    //    dropped on refresh. We do this even if the save is empty/invalid;
    //    the user explicitly asked to navigate back, so don't block them.
    await handleSave();
    setSubmitError(null);
    // 2) Refresh from the backend BEFORE switching groups so the previous
    //    group renders with up-to-date answers and uploaded files (signed
    //    URLs, question.files, etc.) instead of briefly showing stale
    //    state while the network request resolves.
    await dispatch(fetchQuestions());
    setActiveGroupIndex((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitAll = async () => {
    // Validate current group first
    const { errors: currentErrors, cellErrors: currentCellErrors } = validateCurrentGroup();
    if (Object.keys(currentErrors).length > 0) {
      setValidationErrors(currentErrors);
      setTableCellErrors(currentCellErrors);
      return;
    }

    // Validate all groups
    const { errors: allErrors, cellErrors: allCellErrors } = validateAllGroups();
    if (Object.keys(allErrors).length > 0) {
      // Find the first group with errors and navigate to it
      for (let i = 0; i < visibleGroups.length; i++) {
        const groupHasError = visibleGroups[i].questions.some(
          (q) => isQuestionVisible(q) && allErrors[q.id]
        );
        if (groupHasError) {
          setActiveGroupIndex(i);
          setValidationErrors(allErrors);
          setTableCellErrors(allCellErrors);
          return;
        }
      }
    }
    setTableCellErrors({});

    const saved = await handleSave();
    if (saved) {
      await dispatch(completeOnboardingStep(step.id));
      dispatch(fetchOnboardingStatus());
    }
  };

  // Remove an already-uploaded document so a wrong file can be replaced — the
  // client previously had no way to undo an upload (EOP-22).
  const handleRemoveUploadedFile = async (file) => {
    if (!window.confirm(`Remove "${file.original_filename}"? You can upload a replacement afterwards.`)) {
      return;
    }
    const result = await dispatch(deleteAnswerFile(file.id));
    if (result.error) {
      setSubmitError(result.payload || 'Could not remove the document. Please try again.');
    }
  };

  // Saving a draft gave no feedback beyond the transient "Saving..." label, so
  // there was no way to tell it had worked (EOP-15).
  const handleSaveDraft = async () => {
    setDraftSaved(false);
    const saved = await handleSave();
    if (saved) {
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 4000);
    }
  };

  if (loading && questionGroups.length === 0) {
    return (
      <div className="spinner-corporate">
        <div className="spinner-border" role="status" />
        <p>Loading questions...</p>
      </div>
    );
  }

  return (
    <div className="ob-card">
      <div className="ob-card-header">
        <h5>{activeGroup ? activeGroup.name : 'Onboarding Questions'}</h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn-outline-custom" onClick={handleSaveDraft} disabled={loading}>
            {loading ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>

      <div className="ob-card-body">
        {submitError && (
          <div className="alert-corporate danger" style={{ marginBottom: 16 }}>{submitError}</div>
        )}

        {draftSaved && (
          <div className="alert-corporate success" style={{ marginBottom: 16 }}>
            Draft saved — you can safely close this page and continue later.
          </div>
        )}

        {activeGroup && activeGroup.description && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginBottom: 20 }}>
            {activeGroup.description}
          </p>
        )}

        {activeQuestions.map((question) => (
          <div key={question.id} className="question-field">
            <label className="question-label">
              {question.label}
              {question.is_required && <span className="required">*</span>}
              <HelpTip content={question.description} label={`More about: ${question.label}`} />
            </label>
            {question.help_text && (
              <div className="question-help">{question.help_text}</div>
            )}
            <QuestionField
              question={question}
              value={
                // File questions store File objects outside Redux (in
                // fileAnswersRef) and only put a '__files__' marker into
                // state. Surface the actual array so FileUploadField can
                // preview the selection before submit.
                question.type === 'file'
                  ? (fileAnswersRef.current[question.id] || [])
                  : answers[question.id]
              }
              onChange={handleAnswerChange}
              cellErrors={tableCellErrors[question.id]}
              onRemoveUploaded={handleRemoveUploadedFile}
            />
            {validationErrors[question.id] && (
              <div className="question-error">{validationErrors[question.id]}</div>
            )}
            {docValidationErrors[question.id] && (
              <div className="doc-validation-panel">
                {docValidationErrors[question.id].map((failure, i) => (
                  <div key={i} className="doc-validation-failure">
                    <strong>{failure.filename}:</strong> {failure.message}
                  </div>
                ))}
                <label className="doc-validation-justify-label">
                  Upload a corrected document, or explain why this one should be accepted:
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Justification for the reviewer (optional)"
                  value={docJustifications[question.id] || ''}
                  onChange={(e) =>
                    setDocJustifications((prev) => ({ ...prev, [question.id]: e.target.value }))
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="ob-card-footer">
        {!(isFirstStep && isFirstGroup) ? (
          <button
            className="btn-secondary-custom"
            onClick={handlePrevGroup}
            disabled={loading}
          >
            {loading ? 'Loading...' : '← Back'}
          </button>
        ) : <div />}

        {isLastGroup ? (
          <button className="btn-primary-custom" onClick={handleSubmitAll} disabled={loading}>
            {loading ? 'Saving...' : 'Save & Continue \u2192'}
          </button>
        ) : (
          <button className="btn-primary-custom" onClick={handleNextGroup} disabled={loading}>
            {loading ? 'Saving...' : 'Next \u2192'}
          </button>
        )}
      </div>
    </div>
  );
}

export default QuestionsStep;
